import { defineStore } from "pinia";
import { computed, nextTick, ref } from "vue";
import { AuthChannelType } from "@/generated/graphql";
import { useAppStore } from "../app.store";
import {
  useTokenStorage,
  type StoredToken,
} from "./composables/use-token-storage";
import { useCleanupCache } from "@/composables/global/use-cleanup-cache";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { notifySubject, SimpleObservableSubject } from "@/utils/observable";
import type { UserStoreMe } from "../user.store";

export interface SessionSetPayload {
  jwt: string;
  matchesIdentity: boolean;
  authChannelType: AuthChannelType;
}

export interface IdentifiedPayload {
  user: UserStoreMe;
  authChannelType: AuthChannelType;
}

export interface MembershipUpdatePayload {}

/**
 * Auth store for managing authentication state.
 *
 * This store handles:
 * - Token storage and management
 * - Authentication state (hasAuthToken, authMe)
 * - Session lifecycle (onSessionSet, onIdentified)
 * - Login/logout actions
 *
 * Note: This store does NOT handle gating logic - use useGatingEvaluator() for that.
 */
export const useAuthStore = defineStore("auth", () => {
  const appStore = useAppStore();

  // Use session storage for embeds, local storage otherwise
  const authenticationStorage = computed(() =>
    appStore.isEmbed ? sessionStorage : localStorage,
  );

  // Token storage - supports OTP and OAuth auth channels
  const tokenStorage = useTokenStorage(
    {
      supportedAuthChannels: [AuthChannelType.Otp, AuthChannelType.Oauth],
    },
    authenticationStorage.value,
  );

  // Auth state
  const authMe = ref<UserStoreMe | null>(null);
  const initialized = ref(true);

  // Computed auth status
  const hasAuthToken = computed(
    () => tokenStorage.mostRecentToken.value?.matchesIdentity ?? false,
  );

  // External membership update observable (for backwards compatibility during migration)
  const externalMembershipUpdate =
    new SimpleObservableSubject<MembershipUpdatePayload>();

  function onExternalMembershipUpdate(payload: MembershipUpdatePayload) {
    notifySubject(externalMembershipUpdate, payload);
  }

  // Cleanup cache utility
  const { cleanupCache } = useCleanupCache();

  /**
   * Called when a session is created (before user is fully identified).
   * This happens when credentials are validated but user may not exist yet.
   */
  async function onSessionSet(payload: SessionSetPayload): Promise<void> {
    tokenStorage.storeToken({
      jwt: payload.jwt,
      matchesIdentity: payload.matchesIdentity,
      authChannel: payload.authChannelType,
    });

    if (payload.matchesIdentity) {
      await cleanupCache();
    }
  }

  /**
   * Called when a user is identified (final step of authentication).
   * Updates authMe and ensures token is marked as matching identity.
   */
  async function onIdentified(payload: IdentifiedPayload): Promise<void> {
    authMe.value = payload.user;

    const currentToken = tokenStorage.getTokenForChannel(
      payload.authChannelType,
    );
    if (currentToken && currentToken.matchesIdentity === false) {
      tokenStorage.updateTokenForChannel(
        { matchesIdentity: true },
        payload.authChannelType,
      );
      await nextTick();
      await cleanupCache();
    }
  }

  /**
   * Trigger the login flow by showing the auth modal.
   */
  function login(): void {
    const { emitChannelEvent } = useChannelEvents();
    emitChannelEvent({ type: ChannelEventType.SHOW_AUTH_MODAL });
  }

  /**
   * Log out the user by clearing auth state.
   */
  function logout(): void {
    authMe.value = null;
    tokenStorage.clearAllTokens();
  }

  /**
   * Get token for a specific auth channel.
   */
  function getTokenForChannel(
    authChannel: AuthChannelType,
  ): StoredToken | null {
    return tokenStorage.getTokenForChannel(authChannel);
  }

  /**
   * Update token for a specific auth channel.
   */
  function updateTokenForChannel(
    token: Partial<StoredToken>,
    authChannel: AuthChannelType,
  ): void {
    tokenStorage.updateTokenForChannel(token, authChannel);
  }

  return {
    // State
    hasAuthToken,
    authMe,
    initialized,
    tokenStorage,

    // Session lifecycle
    onSessionSet,
    onIdentified,

    // Actions
    login,
    logout,
    getTokenForChannel,
    updateTokenForChannel,

    // External membership update (for migration compatibility)
    externalMembershipUpdate,
    onExternalMembershipUpdate,
  };
});
